/**
 * Escapes characters that could be used for XSS in an HTML context.
 * Useful when interpolating strings into innerHTML or similar APIs.
 * It replaces standard problematic characters (`&`, `<`, `>`, `"`, `'`) with safe HTML entities.
 *
 * @param unsafe - The input string that needs to be evaluated and escaped.
 * @returns {string} The escaped string, safe for HTML interpolation. If `null` or `undefined` is passed, an empty string is returned.
 */
export function escapeHtml(unsafe: string | null | undefined): string {
    if (unsafe == null) return '';
    return String(unsafe)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

/**
 * Sanitizes a URL to ensure it is safe to use as an href.
 * Specifically rejects 'javascript:', 'vbscript:', and 'data:' URIs.
 * If the URL is unsafe, it returns '#' or a safe fallback.
 *
 * @param url - The URL string that needs to be evaluated for safety.
 * @returns {string} The sanitized URL, safe for href/src interpolation. Returns `#` if `url` is unsafe or un-parseable.
 */
export function sanitizeUrl(url: string | null | undefined): string {
    if (!url) return '#';

    // Allow relative URLs, http://, https://, and mailto://
    // Disallow javascript: and data: (unless you specifically need data: images, which are usually handled via src, not href)
    try {
        const parsed = new URL(url, 'http://dummy.base');
        const protocol = parsed.protocol.toLowerCase();

        if (['javascript:', 'vbscript:', 'data:'].includes(protocol)) {
            // For data:, you might allow it if it's strictly image/..., but safer to reject for hrefs
            return '#';
        }

        return escapeHtml(url);
    } catch (e) {
        // If it can't be parsed, be conservative (though relative URLs should parse with the dummy base)
        return '#';
    }
}
